<template>
  <div class="search-panel">
    <div v-if="searchMode === 'fuzzy'" class="search-panel__bar">
      <el-input
        v-model="localKeyword"
        class="search-panel__input"
        placeholder="请输入检索词"
        clearable
        @keyup.enter.native="handleSearch"
      />

      <el-select v-model="searchMode" class="search-panel__mode-select" @change="handleModeChange">
        <el-option label="模糊匹配" value="fuzzy" />
        <el-option label="高级搜索" value="advanced" />
      </el-select>

      <button type="button" class="search-panel__search-btn" @click="handleSearch">
        <i class="el-icon-search" />
      </button>
    </div>

    <div v-else class="search-panel__advanced">
      <div
        v-for="(condition, index) in conditions"
        :key="condition.id"
        class="search-panel__advanced-row"
      >
        <el-select
          v-if="index > 0"
          v-model="condition.logic"
          class="search-panel__mini-select"
        >
          <el-option label="并且" value="and" />
          <el-option label="或" value="or" />
        </el-select>

        <el-select v-model="condition.field" class="search-panel__mini-select">
          <el-option label="资源名称" value="title" />
          <el-option label="作者" value="author" />
        </el-select>

        <el-input
          v-model="condition.keyword"
          class="search-panel__input"
          placeholder="请输入检索词"
          clearable
          @keyup.enter.native="handleSearch"
        />

        <template v-if="index === 0">
          <button type="button" class="search-panel__search-btn" @click="handleSearch">
            <i class="el-icon-search" />
            搜索
          </button>
          <button type="button" class="search-panel__plain-btn" @click="switchToSimple">
            简单检索
          </button>
        </template>
        <button
          v-else
          type="button"
          class="search-panel__icon-btn"
          aria-label="删除条件"
          @click="removeCondition(index)"
        >
          <i class="el-icon-minus" />
        </button>
        <button
          v-if="index === conditions.length - 1 && conditions.length < 3"
          type="button"
          class="search-panel__icon-btn"
          aria-label="添加条件"
          @click="addCondition"
        >
          <i class="el-icon-plus" />
        </button>
      </div>
    </div>

    <div v-if="activeTags.length || filterTags.length" class="search-panel__tags">
      <el-tag
        v-for="tag in filterTags"
        :key="`filter-${tag.type}-${tag.key}`"
        closable
        class="search-panel__tag"
        @close="$emit('remove-filter-tag', tag)"
      >
        {{ tag.label }}
      </el-tag>
      <el-tag
        v-for="tag in activeTags"
        :key="`keyword-${tag}`"
        closable
        class="search-panel__tag"
        @close="$emit('remove-tag', tag)"
      >
        {{ tag }}
      </el-tag>
      <button type="button" class="search-panel__clear-btn" @click="$emit('clear-tags')">
        清除
      </button>
    </div>

    <div class="search-panel__toolbar">
      <span class="search-panel__total">共检索到 <em>{{ total }}</em> 条结果</span>
      <div class="search-panel__view-toggle">
        <button
          type="button"
          class="search-panel__view-btn"
          :class="{ 'search-panel__view-btn--active': viewMode === 'list' }"
          aria-label="列表视图"
          @click="$emit('update:viewMode', 'list')"
        >
          <i class="el-icon-s-operation" />
        </button>
        <button
          type="button"
          class="search-panel__view-btn"
          :class="{ 'search-panel__view-btn--active': viewMode === 'grid' }"
          aria-label="网格视图"
          @click="$emit('update:viewMode', 'grid')"
        >
          <i class="el-icon-s-grid" />
        </button>
      </div>
    </div>
  </div>
</template>

<script>
function createCondition(logic = 'and') {
  return {
    id: `cond-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    logic,
    field: 'title',
    matchMode: 'fuzzy',
    keyword: ''
  }
}

export default {
  name: 'ResourcesSearchPanel',
  props: {
    total: { type: Number, default: 0 },
    activeTags: { type: Array, default: () => [] },
    filterTags: { type: Array, default: () => [] },
    viewMode: { type: String, default: 'list' }
  },
  data() {
    return {
      localKeyword: '',
      searchMode: 'fuzzy',
      conditions: [createCondition()]
    }
  },
  methods: {
    handleModeChange(mode) {
      if (mode === 'advanced') {
        this.ensureAdvancedRows()
        if (this.localKeyword.trim() && !this.conditions[0].keyword) {
          this.conditions[0].keyword = this.localKeyword.trim()
        }
      }
    },
    ensureAdvancedRows() {
      if (!this.conditions.length) {
        this.conditions = [createCondition()]
      }
      if (this.conditions.length === 1) {
        this.conditions.push(createCondition('and'))
      }
    },
    switchToSimple() {
      const firstKeyword = (this.conditions[0] && this.conditions[0].keyword) || ''
      this.searchMode = 'fuzzy'
      if (firstKeyword && !this.localKeyword.trim()) {
        this.localKeyword = firstKeyword
      }
    },
    addCondition() {
      if (this.conditions.length >= 3) {
        return
      }
      this.conditions.push(createCondition('and'))
    },
    removeCondition(index) {
      if (index <= 0 || this.conditions.length <= 1) {
        return
      }
      this.conditions.splice(index, 1)
    },
    handleSearch() {
      if (this.searchMode === 'advanced') {
        const conditions = this.conditions
          .map((item, index) => ({
            logic: index === 0 ? 'and' : (item.logic || 'and'),
            field: item.field === 'author' ? 'author' : 'title',
            keyword: (item.keyword || '').trim()
          }))
          .filter((item) => item.keyword)
        this.$emit('search', {
          searchMode: 'advanced',
          matchMode: 'fuzzy',
          conditions
        })
        return
      }
      this.$emit('search', {
        searchMode: 'fuzzy',
        keyword: this.localKeyword.trim(),
        matchMode: 'fuzzy'
      })
    },
    handleSecondarySearch() {
      this.$emit('secondary-search', {
        keyword: this.localKeyword.trim(),
        matchMode: 'fuzzy'
      })
    },
    reset() {
      this.localKeyword = ''
      this.searchMode = 'fuzzy'
      this.conditions = [createCondition()]
    },
    setKeyword(keyword) {
      this.localKeyword = keyword || ''
      this.searchMode = 'fuzzy'
    },
    setConditions(list) {
      const next = (Array.isArray(list) ? list : [])
        .slice(0, 3)
        .map((item, index) => ({
          ...createCondition(index === 0 ? 'and' : (item.logic || 'and')),
          field: item.field === 'author' ? 'author' : 'title',
          keyword: item.keyword || ''
        }))
      this.conditions = next.length ? next : [createCondition()]
      this.searchMode = 'advanced'
      this.ensureAdvancedRows()
    }
  }
}
</script>

<style lang="scss" scoped>
.search-panel {
  background: $color-white;
  border-radius: 4px;
  padding: $spacing-base;
  box-shadow: 0 2px 12px rgba($color-brown-dark, 0.06);

  &__bar,
  &__advanced-row {
    display: flex;
    align-items: center;
    gap: $spacing-sm;
    flex-wrap: wrap;
  }

  &__advanced {
    display: flex;
    flex-direction: column;
    gap: $spacing-sm;
  }

  &__input {
    flex: 1;
    min-width: 180px;
  }

  &__mode-select {
    width: 120px;
    flex-shrink: 0;
  }

  &__mini-select {
    width: 108px;
    flex-shrink: 0;
  }

  &__search-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 44px;
    height: 36px;
    padding: 0 $spacing-base;
    background: $color-brown-dark;
    color: $color-white;
    border-radius: 4px;
    font-size: rem(14);
    gap: rem(4);
    transition: background 0.2s;
    flex-shrink: 0;

    &:hover {
      background: $primary-color;
    }
  }

  &__plain-btn,
  &__icon-btn {
    height: 36px;
    padding: 0 $spacing-base;
    font-size: rem(14);
    color: $color-brown-dark;
    border: 1px solid $color-border;
    border-radius: 4px;
    background: $color-white;
    flex-shrink: 0;
    transition: all 0.2s;

    &:hover {
      border-color: $color-brown-dark;
      color: $color-brown-dark;
    }
  }

  &__icon-btn {
    width: 36px;
    padding: 0;
    font-size: rem(16);
  }

  &__tags {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: $spacing-sm;
    margin-top: $spacing-base;
    padding-top: $spacing-base;
    border-top: 1px solid rgba($color-border, 0.5);
  }

  &__tag {
    --el-tag-bg-color: rgba($color-paper, 0.8);
    --el-tag-border-color: #{$color-border};
    --el-tag-text-color: #{$color-text};
  }

  &__clear-btn {
    font-size: rem(13);
    color: $success-color;

    &:hover {
      text-decoration: underline;
    }
  }

  &__toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: $spacing-base;
    padding-top: $spacing-base;
    border-top: 1px solid rgba($color-border, 0.5);
  }

  &__total {
    font-size: rem(14);
    color: $color-text-light;

    em {
      font-style: normal;
      font-weight: 600;
      color: $color-brown-dark;
    }
  }

  &__view-toggle {
    display: flex;
    gap: $spacing-xs;
  }

  &__view-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 4px;
    color: $color-text-light;
    font-size: rem(16);
    transition: all 0.2s;

    &:hover {
      background: rgba($color-brown-dark, 0.06);
    }

    &--active {
      background: $color-brown-dark;
      color: $color-white;
    }
  }
}
</style>
